/**
 * THE one place a hex code may appear in this repo.
 *
 * Every value below is lifted verbatim from the code it replaces:
 *  - `palette.light` / `palette.dark` — the two inline theme objects that used
 *    to live in `apps/project-overview/src/main.js` (`light` / `dark`).
 *  - `chart.light` / `chart.dark` — the fallback colours the old PNG-export
 *    helper used when its DOM sniffing found nothing, and the ones the store's
 *    `chartFontColor` getter returned by hand.
 *  - `chart.categorical` / `chart.othersColor` — `ControllerDonutChart.vue`'s
 *    `getRandomColor()` palette and its "Others" slice colour.
 *  - `chart.unassignedColor` — a donut slice with no key ("Unassigned"); new on
 *    2026-10-07 (Daniel: "make unassigned grey"), replacing a random palette pick.
 *  - `chart.series` / `chart.seriesFallback` / `chart.axisPointerLabel` /
 *    `chart.dataZoom` / `chart.loadingOverlay` — the five hard-coded colour
 *    groups the app's own chart SFCs carried before S11: ECharts' default
 *    8-colour palette as inlined in `DocumentFileTypeBarChart.vue`, the grey a
 *    null colour band fell back to, the axis-pointer label background, the zoom
 *    slider's four fills and the burndown chart's loading tint.
 *  - `chart.boxPlot` — `@rdrive/plugin-chart-box-plot`'s colours, lifted from
 *    issue-analysis's own `BoxPlot.vue` when it became the package base
 *    (IA_ABSORPTION_PLAN Phase 4 step 12d).
 *  - `chart.sparkline` — `@rdrive/plugin-total-cards`' spark cards' line and
 *    area-gradient fallbacks per mode, lifted from issue-analysis's own
 *    `TotalSparkCard*.vue` when they were upstreamed (IA_ABSORPTION_PLAN Phase 4
 *    step 13).
 *  - `chart.statusBreakdown` — `@rdrive/plugin-total-cards`' `StatusBreakdownCard`
 *    (issue-analysis's Overview "Issue Status Breakdown" card, its
 *    `components/cards/IssueStatusBarCard.vue` until 2026-10-12) + issue-analysis's
 *    `composables/useIssueStatusBreakdown.js`: a status with no colour, and the
 *    segments the legend hover dims (IA_ABSORPTION_PLAN Phase 4 step 14).
 *  - `chart.gantt` — `@rdrive/plugin-gantt`'s `BaseGanttChart`, per mode: `light`
 *    lifted from issue-analysis's own `components/gantts/IssueDueByGantt.vue` when it
 *    became the package base (IA_ABSORPTION_PLAN Phase 4 step 13b); `dark` new on
 *    2026-10-13, when the Gantt started following the dark theme.
 *  - `chart.categoricalByMode`, `chart.sequential`, `chart.semantic`, `chart.dimOpacity`,
 *    `chart.pin` — asset-management's chart palette, VERBATIM from its standalone
 *    `src/utils/vizPalette.js`, `src/utils/chartTheme.js` (`DIM_OPACITY`) and
 *    `src/components/grids/MiniMap.vue` (AM_ABSORPTION_PLAN step B3, 2026-10-16 — ported so
 *    the app does not recolour). Its axis-line colour is `chart.<mode>.splitLineColor` (the
 *    same in light; 0.12 instead of AM's 0.16 alpha in dark). `chart.dimOpacity` is read by
 *    `StatusBreakdownCard`'s opt-in `selectable` (step C2): the segments it dims.
 *  - `welcome` — the four colours of the welcome dialog's "Discover what's new
 *    from RDrive" panel (`@rdrive/plugin-welcome-dialog`, `showMarketingPanel`),
 *    lifted from issue-analysis's own `WelcomeDialog.vue` when the panel was
 *    upstreamed into the package.
 *
 * Nothing here imports anything: tokens are data, usable from Node, from a
 * Vuetify theme definition and from an ECharts option alike.
 *
 * @typedef {Object} ChartModeTokens
 * @property {string} fontColor       axis/label/legend text colour
 * @property {string} background      canvas background (used by PNG export)
 * @property {string} splitLineColor  grid split-line colour
 *
 * @typedef {Object} DataZoomTokens
 * @property {string} background      slider track background
 * @property {string} dataBackground  the slider's miniature series line/area
 * @property {string} filler          the selected-window fill
 * @property {string} handle          the drag handles
 *
 * @typedef {Object} BoxPlotTokens
 * @property {string} box               the box fill when the page passes no colour
 * @property {string} borderFallback    the box border when the fill cannot be darkened
 * @property {string} tooltipDivider    the tooltip's title rule
 * @property {string} tooltipLabel      the tooltip's grey labels
 * @property {string} tooltipMedian     the tooltip's median value
 * @property {string} paginationDivider the pagination row's top rule
 * @property {string} scrollbarThumb    the breadcrumb row's scrollbar thumb
 *
 * @typedef {Object} SparklineModeTokens
 * @property {string}   line      the line colour when the card is given none
 * @property {string[]} gradient  the area gradient, top → bottom (8-digit hex = colour + alpha),
 *                                when the card is given none
 *
 * @typedef {Object} StatusBreakdownTokens
 * @property {string} fallback  a status segment/dot whose status has no colour
 * @property {string} dimmed    the segments NOT under the legend hover (4-digit hex = grey +
 *                              alpha)
 *
 * @typedef {Object} GanttTokens
 * @property {string} leafRow  the background of a row without children (issue-analysis: an
 *                             issue), both grid halves — one mode's value (`chart.gantt[mode]`)
 * @property {string|null} background        the grid body and panel behind the rows; null = the
 *                                           Bryntum theme's own (light: Material as shipped)
 * @property {string|null} headerBackground  the column and time-axis headers
 * @property {string|null} splitter          the bar between the grid and the timeline
 * @property {string|null} border            row, cell, header and column-line borders
 * @property {string} statusPillText      a status pill's text (asset-management's inspection
 *                                        schedule: its Status column) — the same in both modes
 * @property {string} statusPillFallback  a status pill whose status has no colour
 *
 * @typedef {Object} SemanticTokens
 * @property {string} good     a good outcome / passed (green)
 * @property {string} warning  needs attention / due soon (amber)
 * @property {string} serious  failed / overdue (red)
 * @property {string} neutral  no outcome / not applicable (grey)
 *
 * @typedef {Object} PinTokens
 * @property {string} fill             the drawing-pin marker
 * @property {string} border           the marker's ring
 * @property {string} shadow           the marker's drop shadow
 * @property {string} frameBackground  the drawing thumbnail's frame
 * @property {string} frameBorder      the frame's border
 *
 * @typedef {Object} WelcomeTokens
 * @property {string} panelBackground  the marketing panel's dark band (and the card behind it)
 * @property {string} panelHeading     the "Discover what's new from RDrive" heading
 * @property {string} panelTitle       an item's text title (the one without a logo image)
 * @property {string} panelText        an item's one-line description
 */

/**
 * Design tokens for every rdrive dashboard.
 *
 * @type {{
 *   palette: { light: Record<string, string>, dark: Record<string, string> },
 *   chart: {
 *     light: ChartModeTokens,
 *     dark: ChartModeTokens,
 *     categorical: string[],
 *     othersColor: string,
 *     unassignedColor: string,
 *     series: string[],
 *     seriesFallback: string,
 *     axisPointerLabel: string,
 *     dataZoom: DataZoomTokens,
 *     loadingOverlay: string,
 *     boxPlot: BoxPlotTokens,
 *     sparkline: { light: SparklineModeTokens, dark: SparklineModeTokens },
 *     statusBreakdown: StatusBreakdownTokens,
 *     gantt: { light: GanttTokens, dark: GanttTokens },
 *     categoricalByMode: { light: string[], dark: string[] },
 *     sequential: { light: string[], dark: string[] },
 *     semantic: SemanticTokens,
 *     dimOpacity: number,
 *     pin: PinTokens
 *   },
 *   welcome: WelcomeTokens
 * }}
 */
export const tokens = {
  palette: {
    light: {
      background: '#F5F5F5',
      surface: '#FFFFFF',
      primary: '#1976D2',
      'primary-darken-1': '#1565C0',
      secondary: '#424242',
      'secondary-darken-1': '#212121',
      error: '#D32F2F',
      info: '#2196F3',
      success: '#4CAF50',
      warning: '#FFA000',
    },
    dark: {
      background: '#213542',
      surface: '#263e4e',
      primary: '#fff',
      'primary-darken-1': '#1a292f',
      secondary: '#424242',
      'secondary-darken-1': '#1b1b1b',
      error: '#FF5252',
      info: '#2196F3',
      success: '#4CAF50',
      warning: '#FB8C00',
    },
  },
  chart: {
    light: { fontColor: '#000000', background: '#F5F5F5', splitLineColor: 'rgba(0,0,0,0.12)' },
    dark: { fontColor: '#FFFFFF', background: '#213542', splitLineColor: 'rgba(255,255,255,0.12)' },
    // ControllerDonutChart.vue getRandomColor()
    categorical: [
      '#F44336', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5',
      '#2196F3', '#03A9F4', '#00BCD4', '#009688', '#4CAF50',
      '#8BC34A', '#CDDC39', '#FFC107', '#FF9800', '#FF5722',
    ],
    // ControllerDonutChart.vue — the "Others" aggregate slice
    othersColor: '#999',
    // A donut slice with no key ("Unassigned"): a stable grey, lighter than Others
    unassignedColor: '#BDBDBD',
    // ECharts' own default palette, as hard-coded in DocumentFileTypeBarChart.vue.
    // Kept VERBATIM and in this order so the Documents page does not repaint;
    // #5470c6/#91cc75 are also the team/user bar colours and the uploads line
    // colour. Unifying it with `categorical` is a separate design decision.
    series: [
      '#5470c6', '#91cc75', '#fac858', '#ee6666',
      '#73c0de', '#3ba272', '#fc8452', '#9a60b4',
    ],
    // Both StatusHistory line charts — a colour band whose `color` is null/empty
    seriesFallback: '#CCCCCC',
    // Both StatusHistory line charts — tooltip axis-pointer label background
    axisPointerLabel: '#6a7985',
    // Both StatusHistory line charts — the zoom slider's styling, identical in both
    dataZoom: {
      background: 'rgba(47,69,84,0.1)',
      dataBackground: 'rgba(47,69,84,0.3)',
      filler: 'rgba(167,183,204,0.4)',
      handle: '#a7b7cc',
    },
    // The burndown chart's loading overlay tint
    loadingOverlay: 'rgba(0,0,0,0.1)',
    // @rdrive/plugin-chart-box-plot's BaseBoxPlotChart, the same in both modes — VERBATIM from
    // issue-analysis's components/charts/base/BoxPlot.vue (IA_ABSORPTION_PLAN Phase 4 step 12d)
    boxPlot: {
      // the box fill when the page passes no colour (the old `boxColor` prop default)
      box: '#b8c5f2',
      // the box border when the fill cannot be darkened (adjustColorShade's fallback)
      borderFallback: '#7a9bcc',
      // the tooltip's title rule, its grey labels and the median value (the tooltip keeps
      // ECharts' default white background in both modes, as before)
      tooltipDivider: '#e0e0e0',
      tooltipLabel: '#666',
      tooltipMedian: '#1976d2',
      // the pagination row's top rule and the breadcrumb scrollbar thumb (the old scoped CSS;
      // the base hands them to its style.css as custom properties)
      paginationDivider: 'rgba(0, 0, 0, 0.12)',
      scrollbarThumb: 'rgba(0, 0, 0, 0.2)',
    },
    // @rdrive/plugin-total-cards' TotalSparkCard / TotalSparkCardWithSwitch, per mode — VERBATIM from
    // issue-analysis's components/cards/TotalSparkCard*.vue (IA_ABSORPTION_PLAN Phase 4 step 13): the
    // line colour when the card's `sparklineColor` is empty, and the area gradient when its
    // `sparklineGradient` is null (brighter and more opaque in dark mode)
    sparkline: {
      light: { line: '#1976D2', gradient: ['#1976D250', '#1976D230', '#1976D210'] },
      dark: { line: '#90CAF9', gradient: ['#90CAF960', '#90CAF940', '#90CAF920'] },
    },
    // issue-analysis's "Issue Status Breakdown" card, the same in both modes — VERBATIM from its
    // IssueStatusBarCard.vue (IA_ABSORPTION_PLAN Phase 4 step 14): the fallback for a status with
    // no colour, and the translucent grey of the segments the legend hover does not focus
    statusBreakdown: {
      fallback: '#9E9E9E',
      dimmed: '#9997',
    },
    // @rdrive/plugin-gantt's BaseGanttChart, per mode: the tint of a leaf task's row. `light` is
    // VERBATIM from issue-analysis's components/gantts/IssueDueByGantt.vue (IA_ABSORPTION_PLAN
    // Phase 4 step 13b); `dark` is new on 2026-10-13, when the Gantt started following the dark
    // theme (Bryntum's Classic-Dark stylesheet — the package README "Theme"): the light tint's
    // blue at low alpha (8-digit hex = colour + alpha), so it lifts the dark grid like the light
    // tint shades the white one. The four surface entries (2026-10-13, Daniel: "try tinting it")
    // repaint Classic-Dark's greys in the dashboard's navy — `background` = `palette.dark.surface`
    // (the card the Gantt sits in), `headerBackground` = `palette.dark.background`, `splitter` =
    // `palette.dark['primary-darken-1']`, `border` = `chart.dark.splitLineColor`; light's are null
    // (Material as shipped).
    gantt: {
      light: { leafRow: '#e3f2fd', background: null, headerBackground: null, splitter: null, border: null,
        statusPillText: '#fff', statusPillFallback: '#888' },
      dark: {
        leafRow: '#90CAF926',
        background: '#263e4e',
        headerBackground: '#213542',
        splitter: '#1a292f',
        border: 'rgba(255,255,255,0.12)',
        // AM_ABSORPTION_PLAN D6: asset-management's inspection-schedule Gantt's status pill (an app
        // column renderer), VERBATIM from standalone AM's gantts/InspectionScheduleGantt.vue (the
        // `.gantt-status` text colour and the renderer's fallback) — the same in both modes
        statusPillText: '#fff',
        statusPillFallback: '#888',
      },
    },
    // asset-management's chart palette (AM_ABSORPTION_PLAN step B3, 2026-10-16), VERBATIM from its
    // standalone src/utils/vizPalette.js (`categoricalLight/Dark`, `sequentialLight/Dark`,
    // `statusColors`), src/utils/chartTheme.js (`DIM_OPACITY`) and grids/MiniMap.vue (the pin and
    // the thumbnail frame) — ported so the app keeps its colours. `categoricalByMode`: 6 colours per
    // mode (`chart.categorical` above is the single-mode project-overview palette); `sequential`: a
    // 5-step ramp, lightest → darkest; `semantic`: outcome colours, the same in both modes;
    // `dimOpacity`: the opacity of the marks a cross-filter selection does not include.
    categoricalByMode: {
      light: ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300'],
      dark: ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300'],
    },
    sequential: {
      light: ['#86b6ef', '#4f92e0', '#2a78d6', '#1a5aa8', '#0d366b'],
      dark: ['#8cbcf2', '#5d9ae6', '#3987e5', '#276cbe', '#184f95'],
    },
    semantic: {
      good: '#0ca30c',
      warning: '#fab219',
      serious: '#d03b3b',
      neutral: '#898781',
    },
    dimOpacity: 0.3,
    pin: {
      fill: '#d03b3b',
      border: '#ffffff',
      shadow: 'rgba(0, 0, 0, 0.6)',
      frameBackground: 'rgba(127, 127, 127, 0.08)',
      frameBorder: 'rgba(127, 127, 127, 0.25)',
    },
  },
  // @rdrive/plugin-welcome-dialog's marketing panel (`showMarketingPanel`), the same in both
  // modes — VERBATIM from issue-analysis's WelcomeDialog.vue (`.interested-section`/
  // `.welcome-panel` background, `.interested-title`, `.interested-item-title`,
  // `.interested-item-desc`). The dialog hands them to its CSS as custom properties.
  welcome: {
    panelBackground: '#16212a',
    panelHeading: 'rgba(255, 255, 255, 0.75)',
    panelTitle: '#ffffff',
    panelText: 'rgba(255, 255, 255, 0.6)',
  },
};
